import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import { isSameDay, setHours, startOfDay } from "@clipboard-health/date-time";
import { isAfter, isBefore, parseISO } from "date-fns";

import { type PlacementIncludedInterview } from "../../../types/fetchPlacements.schema";
import { isInterviewLateCancellation } from "../../../utils/reliabilityRule";

interface UseInterviewSchedulingReliabilityParams {
  interview: PlacementIncludedInterview | undefined;
  workplaceTimezone: string;
}

/**
 * Calculates the workplace start hour as a UTC Date for a given interview day.
 * Uses timezone-aware functions to determine when the workplace's configured
 * start hour occurs on the interview date.
 */
function calculateWorkplaceStartHourUtc(params: {
  interviewStart: Date;
  workplaceTimezone: string;
  startHour: number;
}): Date {
  const { interviewStart, workplaceTimezone, startHour } = params;
  // Get the start of the interview day in the workplace timezone
  const interviewDayStart = startOfDay(interviewStart, { timeZone: workplaceTimezone });
  // Set the hour to the workplace start hour - returns UTC
  return setHours(interviewDayStart, startHour, { timeZone: workplaceTimezone });
}

/**
 * Calculates timezone and timing values for reliability rules.
 * Determines if cancellation/rescheduling is "late" based on workplace rules.
 */
export function useInterviewSchedulingReliability(params: UseInterviewSchedulingReliabilityParams) {
  const { interview, workplaceTimezone } = params;

  const interviewStart = interview ? parseISO(interview.attributes.start) : undefined;

  // Calculate workplace start hour for today reschedule checks (as UTC Date)
  const workplaceStartHourUtc = interviewStart
    ? calculateWorkplaceStartHourUtc({
        interviewStart,
        workplaceTimezone,
        startHour: HIRING_RELIABILITY_CONFIG.WORKPLACE_START_HOUR,
      })
    : undefined;

  const isReschedulingPastInterview =
    interviewStart !== undefined && isBefore(interviewStart, new Date());

  const isSchedulingPastWorkplaceStartHour = workplaceStartHourUtc
    ? isAfter(new Date(), workplaceStartHourUtc)
    : false;

  // Use the simplified reliability rule utility
  const isLateCancellation =
    interview && interviewStart
      ? isInterviewLateCancellation({
          interviewStart,
          interviewCreatedAt: interview.attributes.createdAt,
          isSchedulingPastWorkplaceStartHour,
        })
      : false;

  // Check if interview is today in the workplace timezone
  const startsInWorkplaceToday =
    interviewStart !== undefined &&
    isSameDay(new Date(), interviewStart, { timeZone: workplaceTimezone });

  const isSchedulingTodayAfterWorkplaceStartHour = Boolean(
    workplaceStartHourUtc &&
    interview &&
    isAfter(new Date(), workplaceStartHourUtc) &&
    startsInWorkplaceToday
  );

  return {
    workplaceStartHour: workplaceStartHourUtc,
    isReschedulingPastInterview,
    isSchedulingPastWorkplaceStartHour,
    isLateCancellation,
    startsInWorkplaceToday,
    isSchedulingTodayAfterWorkplaceStartHour,
  };
}

export type InterviewSchedulingReliabilityReturn = ReturnType<
  typeof useInterviewSchedulingReliability
>;
